// @flow
import 'isomorphic-fetch'
import { API, createDefaultHeaders } from '../../util/api'
import { isInvalid } from '@rachio-npm/com-rachio-web-core/util/validation'
import * as actionCreators from './index'
import * as locationSchema from './schemas'
import changeCase from 'change-object-case'

// Types
import type { Dispatch } from '../../util/redux'
import type {
  LocationDeleteCompleteAction,
  LocationUpdateCompleteAction
} from './index'

/**
 * A module that exports `create`, `get`, and `update` functions for locations
 * @module
 */

const Api = new API()

/**
 * Create location
 * @param {string} userAuthToken - User's authorization token
 * @param {object} locationData - Location data
 * @returns {object} An action related to the location create process
 */
export const create = (userAuthToken: string, locationData: Object) => {
  return function(dispatch: Dispatch): Promise<*> {
    const reqData = changeCase.snakeKeys(locationData, { recursive: true })
    const error = isInvalid(locationSchema.LocationCreateRequestSchema, reqData)
    const reqObj = {
      method: 'POST',
      body: JSON.stringify(reqData),
      headers: new Headers({
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      })
    }

    dispatch(actionCreators.locationCreateInit(reqData))

    if (error) {
      return Promise.reject(
        dispatch(actionCreators.locationCreateComplete(error))
      )
    }

    return Api.call('location/createLocation', reqObj)
      .then(response => {
        if (response.status === 409) {
          throw new Error('Location already exist in system')
        }
        return response
      })
      .then(response => response.json())
      .then(json => dispatch(actionCreators.locationCreateComplete(json)))
      .catch(err => dispatch(actionCreators.locationCreateComplete(err)))
  }
}

/**
 * Get location
 * @param {string} userAuthToken - User's authorization token
 * @param {string} locationId - Location id
 * @param {boolean} includeMetadata - Whether or not the response should include metadata
 * @returns {object} An action related to the location retrieval process
 */
export function get(
  userAuthToken: string,
  locationId: string,
  includeMetadata: ?boolean
) {
  return function(dispatch: Dispatch) {
    const reqObj = {
      method: 'GET',
      headers: createDefaultHeaders(userAuthToken)
    }

    dispatch(actionCreators.locationGetInit({ locationId, includeMetadata }))

    return Api.call(
      `location/getLocation/${locationId}${
        includeMetadata === false ? '' : '/true'
      }`,
      reqObj
    )
      .then(response => response.json())
      .then(json => {
        dispatch(actionCreators.up(json.locationSummary))
        return dispatch(
          actionCreators.locationGetComplete(json.locationSummary)
        )
      })
      .catch(err => dispatch(actionCreators.locationGetComplete(err)))
  }
}

/**
 * Update location on the server
 * @param  {string} userAuthToken - User auth token
 * @param  {object} locationData -  Location data to update
 * @return {promise} - Action related to updating a location
 */
export const update = (
  userAuthToken: string,
  locationData: Object
): ((dispatch: Dispatch) => Promise<LocationUpdateCompleteAction>) => {
  return function(dispatch: Dispatch) {
    const reqData = changeCase.snakeKeys(locationData, { recursive: true })
    const error = isInvalid(locationSchema.LocationUpdateRequestSchema, reqData)
    const reqObj = {
      method: 'PUT',
      headers: {
        ...createDefaultHeaders(userAuthToken),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(reqData)
    }

    dispatch(actionCreators.locationUpdateInit(locationData))

    if (error) {
      return Promise.reject(
        dispatch(actionCreators.locationUpdateComplete(error))
      )
    }

    return Api.call('location/updateLocation', reqObj)
      .then(response => response.json())
      .then(json => {
        dispatch(actionCreators.up(json))
        return dispatch(actionCreators.locationUpdateComplete(json))
      })
      .catch(err => {
        err.locationId = locationData.locationId
        return dispatch(actionCreators.locationUpdateComplete(err))
      })
  }
}

/**
 * Delete location
 * @param {string} userAuthToken - User's authorization token
 * @param {string} locationId - Location id
 * @returns {object} An action related to the location deletion process
 */
export const del = (
  userAuthToken: string,
  locationId: string
): ((dispatch: Dispatch) => Promise<LocationDeleteCompleteAction>) => {
  return function(dispatch: Dispatch) {
    const reqObj = {
      method: 'POST',
      headers: createDefaultHeaders(userAuthToken)
    }

    dispatch(actionCreators.locationDeleteInit({ locationId }))

    return Api.call(`location/deleteLocation/${locationId}`, reqObj)
      .then(response => response.json())
      .then(json => {
        dispatch(actionCreators.remove(locationId))
        return dispatch(
          actionCreators.locationDeleteComplete({
            ...json,
            locationId: locationId
          })
        )
      })
      .catch(err => {
        err.locationId = locationId
        return dispatch(actionCreators.locationDeleteComplete(err))
      })
  }
}
